// A photo for a character - shared by the wizard (wizard.js), "Deine Helden" (book.js) and the unpaid
// book page (book_unpaid.html, initialised here): the checks, the preview and, on the book pages, the
// whole way from the tips to the upload.
//
// The preview (Dennis, 2026-10-13): the WHOLE photo, small, with the chosen 3:4 frame drawn on it and a
// veil around it - so you see what was chosen; a tap opens the crop again. The original stays in the
// browser's memory only; what is uploaded is the crop (crop.js). Without the original (a restored form)
// the crop itself is shown, as a rectangle.
//
//   photoProblem(file)                  -> '' or a friendly German line (type, size)
//   previewHtml(crop)                   -> the preview's HTML for a crop (a File from cropPhoto)
//   addPhoto({name, url, csrf, note, button})
//                                       -> the book pages: tips (once) -> file picker -> crop -> consent ->
//                                          upload (multipart: photo + consent). Resolves the JSON answer, or null.
const {cropPhoto} = require('./crop');

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
const pct = (v) => `${(Math.max(0, Math.min(1, v)) * 100).toFixed(2)}%`;

function photoProblem(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    return file.type.startsWith('image/')
      ? 'Dieses Bildformat geht leider nicht – bitte ein JPG, PNG oder WebP wählen (z. B. ein Foto aus der Galerie).'
      : 'Das ist kein Foto. Bitte wähle ein Bild (JPG, PNG oder WebP).';
  }
  if (file.size > 25 * 1024 * 1024) return 'Dieses Foto ist sehr groß (über 25 MB). Bitte wähle ein kleineres.';
  return '';
}

// the whole photo with the frame on it; `url` = the crop's own URL for the fallback (no original)
function previewHtml(crop, url = '') {
  if (crop && crop.frame && crop.sourceUrl) {
    const f = crop.frame;
    return `<span class="photo-fit"><span class="photo-whole" style="--r:${crop.ratio.toFixed(4)}">
      <img src="${esc(crop.sourceUrl)}" alt="Dein Foto mit dem gewählten Ausschnitt">
      <span class="photo-sel" style="left:${pct(f.x)};top:${pct(f.y)};width:${pct(f.w)};height:${pct(f.h)}"></span></span></span>`;
  }
  return url ? `<span class="photo-fit"><img class="photo-crop" src="${esc(url)}" alt="Gewählter Ausschnitt"></span>` : '';
}

// --- the book pages: tips -> picker -> crop -> consent -> upload --------------------------------------
let tipsSeen = false;
let dialog = null;
let picker = null;

function tips() {
  const box = document.querySelector('[data-tips-dialog]');
  if (!box || tipsSeen) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (go) => {
      tipsSeen = true;
      box.querySelector('[data-tips-go]').onclick = null;
      box.querySelector('[data-tips-close]').onclick = null;
      box.onclose = null;
      if (box.open) box.close();
      resolve(go);
    };
    box.querySelector('[data-tips-go]').onclick = () => done(true);
    box.querySelector('[data-tips-close]').onclick = () => done(false);
    box.onclose = () => done(false);
    box.showModal();
  });
}

// the file picker; resolves a File or null. Must start from a click (a user gesture).
function pick() {
  if (!picker) {
    picker = document.createElement('input');
    picker.type = 'file';
    picker.accept = 'image/jpeg,image/png,image/webp';
    picker.hidden = true;
    document.body.appendChild(picker);
  }
  picker.value = '';
  return new Promise((resolve) => {
    picker.onchange = () => resolve(picker.files[0] || null);
    picker.oncancel = () => resolve(null);
    picker.click();
  });
}

function build() {
  dialog = document.createElement('dialog');
  dialog.className = 'sheet photo-dialog';
  dialog.innerHTML = `<form method="dialog" class="p-5 sm:p-7">
    <div class="flex items-start justify-between gap-4">
      <h2 class="text-2xl font-semibold" data-pd-title></h2>
      <button type="button" class="btn-ghost -mr-2 -mt-1 text-xl" data-pd-cancel aria-label="Abbrechen">✕</button>
    </div>
    <button type="button" class="photo-dialog-preview mt-4" data-pd-recrop aria-label="Ausschnitt ändern"></button>
    <div class="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm">
      <button type="button" class="font-semibold text-coral-deep underline-offset-2 hover:underline" data-pd-recrop>✂️ Ausschnitt ändern</button>
      <button type="button" class="font-semibold text-coral-deep underline-offset-2 hover:underline" data-pd-other>📷 Anderes Foto</button>
    </div>
    <p class="mt-4 text-sm text-ink-soft" data-pd-note></p>
    <label class="mt-4 flex cursor-pointer items-start gap-3 rounded-3xl bg-sky-soft p-4">
      <input type="checkbox" class="mt-1 h-5 w-5 accent-coral" data-pd-consent>
      <span class="text-sm text-night">Ich darf das Foto verwenden (bei Kindern: ich bin erziehungsberechtigt oder habe die Zustimmung).
        Das Foto wird nur genutzt, um die Figur zu zeichnen.</span>
    </label>
    <p class="photo-error" role="alert" data-pd-error hidden></p>
    <div class="mt-5 flex flex-wrap justify-end gap-2">
      <button type="button" class="btn-secondary" data-pd-cancel>Abbrechen</button>
      <button type="button" class="btn-primary" data-pd-go></button>
    </div>
  </form>`;
  document.body.appendChild(dialog);
}

async function addPhoto({name, url, csrf, note = '', button = 'Foto speichern'}) {
  if (!(await tips())) return null;
  let file = await pick();
  if (!file) return null;
  if (!dialog) build();
  const q = (sel) => dialog.querySelector(sel);
  const error = (message) => { q('[data-pd-error]').textContent = message; q('[data-pd-error]').hidden = !message; };
  q('[data-pd-title]').textContent = `Foto von ${name}`;
  q('[data-pd-note]').textContent = note;
  q('[data-pd-note]').hidden = !note;
  q('[data-pd-go]').textContent = button;
  q('[data-pd-consent]').checked = false;
  error('');

  let crop = null;
  // crop (again); false when there is nothing to upload any more
  async function cropIt(again = false) {
    while (true) {
      const problem = photoProblem(file);
      let result = problem ? {error: problem} : await cropPhoto(file, {frame: again && crop ? crop.frame : null});
      if (result && result.other) {
        const next = await pick();
        if (next) { file = next; again = false; continue; }
        result = null;
      }
      if (result && result.error) {              // shown in the dialog, with "Anderes Foto"
        error(result.error);
        return true;
      }
      if (result instanceof File) {
        if (crop && crop.sourceUrl !== result.sourceUrl) URL.revokeObjectURL(crop.sourceUrl);
        crop = result;
        q('.photo-dialog-preview').innerHTML = previewHtml(crop);
        error('');
      }
      return !!crop || !!q('[data-pd-error]').textContent;
    }
  }
  q('.photo-dialog-preview').innerHTML = '';
  if (!(await cropIt())) return null;

  return new Promise((resolve) => {
    const finish = (answer) => {
      dialog.onclick = null;
      dialog.onclose = null;
      if (dialog.open) dialog.close();
      if (crop) URL.revokeObjectURL(crop.sourceUrl);
      resolve(answer);
    };
    dialog.onclose = () => finish(null);
    dialog.onclick = async (e) => {
      const t = e.target.closest('button');
      if (!t) return;
      if (t.matches('[data-pd-cancel]')) finish(null);
      else if (t.matches('[data-pd-recrop]') && crop) await cropIt(true);
      else if (t.matches('[data-pd-other]')) {
        const next = await pick();
        if (next) { const before = file; file = next; if (!(await cropIt())) file = before; }
      } else if (t.matches('[data-pd-go]')) {
        if (!crop) { error('Bitte wähle zuerst ein Foto.'); return; }
        if (!q('[data-pd-consent]').checked) { error('Bitte bestätige, dass du das Foto verwenden darfst.'); return; }
        t.disabled = true;
        const body = new FormData();
        body.append('photo', crop, crop.name);
        body.append('consent', '1');
        let data = {};
        let ok = false;
        try {
          const response = await fetch(url, {method: 'POST', body, headers: {'X-CSRFToken': csrf, Accept: 'application/json'}});
          data = await response.json().catch(() => ({}));
          ok = response.ok;
        } catch (err) {
          data = {message: 'Keine Verbindung – bitte gleich noch einmal versuchen.'};
        }
        t.disabled = false;
        if (ok) finish(data);
        else error(data.message || 'Das hat nicht geklappt.');
      }
    };
    dialog.showModal();
  });
}

// --- the unpaid book page: "📷 Foto hinzufügen" / "Foto ändern" on each hero card ----------------------
document.addEventListener('DOMContentLoaded', () => {
  const page = document.querySelector('[data-book-unpaid]');
  if (!page) return;
  page.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-photo-add]');
    if (!b) return;
    const answer = await addPhoto({
      name: b.dataset.name, url: b.dataset.url, csrf: page.querySelector('[name=csrfmiddlewaretoken]').value,
      note: 'Gemalt wird erst nach dem Kauf – dann zeichnen wir die Figur einmal nach diesem Foto.',
    });
    if (answer) {
      try { sessionStorage.setItem('mh-photo-saved', answer.message || ''); } catch (err) { /* no note then */ }
      location.reload();
    }
  });
  let saved = '';
  try { saved = sessionStorage.getItem('mh-photo-saved'); sessionStorage.removeItem('mh-photo-saved'); } catch (err) { /* none */ }
  if (saved) {
    const note = page.querySelector('[data-photo-saved]');
    if (note) { note.textContent = `✓ ${saved}`; note.hidden = false; }
  }
});

module.exports = {addPhoto, photoProblem, previewHtml};
